import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { LocalStorageClass } from '../helpers/local-storage-helper';
import { AuthService } from '../services/auth/auth.service';

/**
 * Reconciles the session tenant with a deep link's `?tenant=` flag (Airbridge
 * desktop fallback). A user can belong to multiple tenants at once and the session
 * token is tenant-scoped, so a shared link for tenant X must switch the session to X
 * before the target content is opened.
 *
 * This only fires when `?tenant=` is present (i.e. external/shared links) — normal
 * in-app navigation has no such param and short-circuits immediately.
 *
 * Runs after `AuthGuard`, so a token is always present here.
 */
export const tenantGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const target = route.queryParamMap.get('tenant');

  // No tenant flag, or already on the right tenant → nothing to do.
  if (!target || target === LocalStorageClass.getTenantIdentifier()) {
    return true;
  }

  return authService.getCurrentUserTenants().pipe(
    switchMap((response) => {
      const tenant = response.tenants.find((t) => t.tenant_identifier === target);

      // User isn't a member of the linked tenant — fall back to the dashboard.
      if (!tenant) {
        return of(router.createUrlTree(['/feed']));
      }

      // Already authenticated against the target tenant — just sync local state.
      if (response.currentTenant === target) {
        LocalStorageClass.setTenantIdentifier(target);
        return of(true);
      }

      // Switch the token to the target tenant, then reload so the app re-bootstraps
      // (user, branding, config) cleanly under the new tenant. On the reloaded
      // navigation the short-circuit above passes immediately.
      return authService.switchCurrentUserTenants(tenant).pipe(
        map((switchResponse) => {
          LocalStorageClass.setToken(switchResponse.token, switchResponse.refreshToken);
          LocalStorageClass.setTenantIdentifier(target);
          window.location.reload();
          return false;
        }),
      );
    }),
    catchError(() => of(router.createUrlTree(['/feed']))),
  );
};
